import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

const DRAG_THRESHOLD_PX = 6;
const CARD_SELECTOR = ".car-insuranceCardB2B";

/**
 * Sincroniza el estado visual `--selected` de `CarInsuranceCardB2B` con su
 * radio button, y permite marcar/desmarcar haciendo clic en cualquier parte
 * de la tarjeta (no solo en el pequeño icono del radio), ya que no tiene
 * ningún otro control.
 *
 * IMPORTANTE — dentro de `CarSummaryExtrasLayout` estas cards viven en un
 * carrusel de scroll horizontal por arrastre (`customHorizontalScroll.ts`).
 * Ese plugin llama a `setPointerCapture` en `pointerdown` sobre cualquier
 * contenido que no sea `a/button/input/label/...`, lo que retarget-ea el
 * `target` de TODOS los eventos posteriores (incluido `click`) al propio
 * contenedor de scroll — así que `click.target` no es fiable aquí. En
 * cambio, el `target` del `pointerdown` original sí llega intacto (la
 * captura se activa DESPUÉS de que ese evento ya se haya despachado), así
 * que la tarjeta implicada se resuelve en el `pointerdown`, no en el
 * `click`; el `click` solo se usa para medir la distancia recorrida
 * (`clientX/Y`, que nunca se corrompe) y así distinguir un clic real de un
 * arrastre de scroll.
 *
 * Cada tarjeta se selecciona de forma independiente ("selecciona uno o
 * varios"), así que un clic sobre una tarjeta ya seleccionada debe
 * deseleccionarla. Un `<input type="radio">` nativo nunca se desmarca al
 * volver a hacer clic sobre él — solo se desmarca cuando otro radio del
 * mismo grupo se marca — así que aquí se gestiona ese caso manualmente.
 * Esto se salta cuando el radio tiene un `name` real compartido con otros
 * (grupo mutuamente excluyente genuino), para no romper ese comportamiento
 * nativo si el componente se reutiliza así en el futuro.
 */
export class CarInsuranceCardSelectionPlugin implements Plugin {
  private readonly radio: HTMLInputElement | null;
  private pointerDownPos: { x: number; y: number } | null = null;
  private wasCheckedBeforePointerDown = false;

  constructor(private readonly element: HTMLElement) {
    this.radio = element.querySelector('input[type="radio"]');
  }

  init(): void {
    if (!this.radio) return;
    document.addEventListener("change", this.handleDocumentChange);
    document.addEventListener("pointerdown", this.handleDocumentPointerDown);
    document.addEventListener("click", this.handleDocumentClick);
    this.syncFromChecked(this.radio.checked);
  }

  destroy(): void {
    document.removeEventListener("change", this.handleDocumentChange);
    document.removeEventListener("pointerdown", this.handleDocumentPointerDown);
    document.removeEventListener("click", this.handleDocumentClick);
  }

  private readonly handleDocumentPointerDown = (event: PointerEvent): void => {
    if (!this.radio) return;

    const target = event.target as HTMLElement;
    if (target.closest(CARD_SELECTOR) !== this.element) {
      this.pointerDownPos = null;
      return;
    }

    this.pointerDownPos = { x: event.clientX, y: event.clientY };
    this.wasCheckedBeforePointerDown = this.radio.checked;
  };

  private readonly handleDocumentClick = (event: MouseEvent): void => {
    if (!this.radio || this.radio.name || !this.pointerDownPos) return;

    const dx = event.clientX - this.pointerDownPos.x;
    const dy = event.clientY - this.pointerDownPos.y;
    this.pointerDownPos = null;
    if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) return;

    if (this.wasCheckedBeforePointerDown) {
      // Ya estaba seleccionada antes de este clic: un radio nativo no se
      // desmarca solo con volver a hacer clic, así que lo forzamos.
      this.radio.checked = false;
      this.radio.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    if (!this.radio.checked) {
      // El clic no aterrizó sobre el radio/su label nativo (que ya la
      // habría marcado por sí solo): la marcamos manualmente.
      this.radio.checked = true;
      this.radio.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  private readonly handleDocumentChange = (event: Event): void => {
    if (!this.radio) return;
    const target = event.target as HTMLInputElement;
    if (target.type !== "radio") return;

    if (target === this.radio) {
      this.syncFromChecked(target.checked);
      return;
    }

    const isRealGroup = !!this.radio.name && target.name === this.radio.name;
    if (isRealGroup) {
      this.syncFromChecked(false);
    }
  };

  private syncFromChecked(isSelected: boolean): void {
    this.element.classList.toggle("car-insuranceCardB2B--selected", isSelected);
  }
}

PluginRegistry.register(
  "car-insuranceCard-selection",
  CarInsuranceCardSelectionPlugin,
);
